import { Trans } from "@lingui/macro";
import {
  IApiEpBillingPaymentProvider,
  IApiEpBillingProductPurchaseRes,
} from "@smp/api";
import { UiBox, UiButton, UiTypography } from "@smp/ui";
import { REACT_APP__BASE_PATH } from "@smp/utils";
import { useCallback, useLayoutEffect, useMemo, useRef } from "react";

import { useWindowOpener } from "@/hooks/use_window_opener";

import { isRecurringPayment } from "../payment_v2.static";

// Component for paypal, qiwi, maib
export const ExternalWindow = ({
  invoice,
  provider,
  onDecline,
  onSuccess,
}: {
  invoice: IApiEpBillingProductPurchaseRes;
  provider: IApiEpBillingPaymentProvider;
  onDecline: () => void;
  onSuccess?: () => void;
}) => {
  const isSuccessPageOpened = useRef(false);

  let url = useMemo(() => {
    const providerId = isRecurringPayment(invoice)
      ? invoice.recurring_payment.provider_id
      : invoice.bill.payment_provider;

    if (providerId === "qiwi") {
      const paymentData = isRecurringPayment(invoice)
        ? invoice.recurring_payment
        : invoice.bill;

      const urlOrigin = window.location.origin;
      const urlFail =
        urlOrigin + REACT_APP__BASE_PATH + "/public/purchase/fail";
      const urlSuccess =
        urlOrigin +
        REACT_APP__BASE_PATH +
        "/public/purchase/success?auto_close=1&bill=" +
        (isRecurringPayment(invoice)
          ? invoice.recurring_payment.id
          : paymentData.id);

      const params = new URLSearchParams();

      params.set("successUrl", urlSuccess);
      params.set("failUrl", urlFail);

      if (isRecurringPayment(invoice)) {
        params.set(
          "from",
          invoice.recurring_payment.confirm_widget_args.publicId,
        );
        params.set(
          "summ",
          String(invoice.recurring_payment.confirm_widget_args.amount),
        );

        params.set(
          "currency",
          invoice.recurring_payment.confirm_widget_args.currency,
        );
        params.set(
          "comm",
          invoice.recurring_payment.confirm_widget_args.description,
        );
        params.set("txn_id", String(invoice.recurring_payment.id));
      } else {
        const publicId =
          provider.currencies_supported[invoice.bill.currency]?.public_id;

        if (publicId) {
          params.set("from", publicId);
          params.set("summ", invoice.bill.amount);
          params.set("currency", invoice.bill.currency);
          params.set("comm", invoice.bill.description);
          params.set("txn_id", String(invoice.bill.id));
        }
      }

      return `https://w.qiwi.com/order/external/create.action?${params.toString()}`;
    }

    if (isRecurringPayment(invoice)) {
      return invoice.recurring_payment.confirm_url;
    }

    return {
      paypal: invoice.bill.extra.paypal_order_approve_url,
      maib: invoice.bill.extra.maib_transaction_approve_url,
    }[invoice.bill.payment_provider];
  }, [invoice, provider.currencies_supported]);

  const { isOpen, open } = useWindowOpener(
    {
      url,
    },
    {
      onClose: () => {
        if (isSuccessPageOpened.current) {
          onSuccess?.();
        } else {
          onDecline();
        }
      },
    },
  );

  const handleMessage = useCallback<(event: MessageEvent<any>) => void>(
    (event) => {
      if (event.data?.type === "billing_success_page_open") {
        isSuccessPageOpened.current = true;
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (isOpen) {
      window.addEventListener("message", handleMessage);

      return () => window.removeEventListener("message", handleMessage);
    }
  }, [handleMessage, isOpen]);

  return isOpen ? (
    <UiBox>
      <UiBox>
        <UiTypography>
          <Trans>Payment in progress, do not close this window...</Trans>
        </UiTypography>
      </UiBox>
    </UiBox>
  ) : (
    <UiBox>
      <UiBox mb={2}>
        <UiTypography>
          <Trans>
            A payment invoice has been created, please proceed to payment.
          </Trans>
        </UiTypography>
      </UiBox>
      <UiBox>
        <UiButton color="primary" onClick={open} variant="contained">
          <Trans>Proceed</Trans>
        </UiButton>
      </UiBox>
    </UiBox>
  );
};
